import { createFileRoute } from '@tanstack/react-router'
import { AdminShell, adminPage } from '#/components/admin/admin-shell'
import { DailyChart } from '#/components/admin/charts'
import { KV, Panel, StatTile, fmtTokens, fmtUsd } from '#/components/admin/ui'
import { Badge } from '#/components/ui/badge'
import { useAdminDaily, useAdminHealth, useAdminSummary } from '#/lib/api-admin'
import { fmtCurrency, fmtNumber, fmtPercent } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/admin/dashboard')({ component: adminPage(Page) })

function Page() {
  const { data: s } = useAdminSummary()
  const { data: d } = useAdminDaily(30)
  const { data: h } = useAdminHealth()
  const days = d?.days ?? []
  const revenue = s?.revenue30d ?? []
  return (
    <AdminShell title={m.admin_nav_overview()} description={m.admin_overview_desc()} wide>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={m.admin_stat_users()} value={s ? fmtNumber(s.users.total) : '…'} sub={s ? m.admin_stat_users_sub({ d7: fmtNumber(s.users.new7d), d30: fmtNumber(s.users.new30d) }) : null} />
        <StatTile label={m.admin_stat_plans()} value={s ? fmtNumber(s.plans.total) : '…'} sub={s ? m.admin_stat_plans_sub({ prds: fmtNumber(s.plans.prdsGenerated), d30: fmtNumber(s.plans.new30d) }) : null} />
        <StatTile label={m.admin_stat_revenue()} value={s ? (revenue.length ? revenue.map((r) => fmtCurrency(r.gross - r.discount, r.currency === 'USD' ? 'USD' : 'IDR')).join(' · ') : '0') : '…'} sub={s ? m.admin_stat_revenue_sub({ n: fmtNumber(revenue.reduce((a, r) => a + r.count, 0)) }) : null} />
        <StatTile label={m.admin_stat_llm()} value={s ? fmtUsd(s.llm30d.costUsd) : '…'} sub={s ? m.admin_stat_llm_sub({ jobs: fmtNumber(s.llm30d.jobs), tokens: fmtTokens(s.llm30d.jobInputTokens + s.llm30d.jobOutputTokens + s.llm30d.chatInputTokens + s.llm30d.chatOutputTokens) }) : null} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={m.admin_stat_verified_week()} value={s ? fmtNumber(s.funnel30d.verifiedPerWeek) : '…'} />
        <StatTile label={m.admin_stat_activation()} value={s ? fmtPercent(s.funnel30d.activation) : '…'} />
        <StatTile label={m.admin_stat_evidence_ratio()} value={s ? fmtPercent(s.funnel30d.evidenceRatio) : '…'} />
        <StatTile label={m.admin_stat_exports()} value={s ? `${fmtNumber(s.funnel30d.exports)} · ${fmtNumber(s.funnel30d.shareViews)}` : '…'} />
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <DailyChart data={days} dataKey="signups" kind="line" title={m.admin_chart_signups()} />
        <DailyChart data={days} dataKey="plans" kind="line" title={m.admin_chart_plans()} />
        <DailyChart data={days} dataKey="revenueIdr" kind="bar" title={m.admin_chart_revenue_idr()} format={(n) => fmtCurrency(n, 'IDR')} />
        <DailyChart data={days} dataKey="costUsd" kind="bar" title={m.admin_chart_cost()} format={fmtUsd} />
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <Panel title={m.admin_active_subs()}>
          {s?.activeSubscriptions.length ? <KV items={s.activeSubscriptions.map((a) => ({ k: a.name, v: fmtNumber(a.count) }))} /> : <p className="text-sm text-muted-foreground">{m.admin_empty()}</p>}
        </Panel>
        <Panel title={m.admin_llm_breakdown()}>
          {s ? (
            <KV
              items={[
                { k: m.admin_llm_jobs(), v: `${fmtNumber(s.llm30d.jobs)} · ${fmtTokens(s.llm30d.jobInputTokens)} in / ${fmtTokens(s.llm30d.jobOutputTokens)} out` },
                { k: m.admin_llm_chat(), v: `${fmtNumber(s.llm30d.chatMessages)} · ${fmtTokens(s.llm30d.chatInputTokens)} in / ${fmtTokens(s.llm30d.chatOutputTokens)} out` },
                { k: m.admin_col_cost(), v: fmtUsd(s.llm30d.costUsd) },
              ]}
            />
          ) : null}
        </Panel>
        <Panel title={m.admin_funnel_title()} className="md:col-span-3">
          {s ? (
            <KV
              items={[
                { k: m.admin_funnel_signups(), v: fmtNumber(s.funnel30d.signups) },
                { k: m.admin_funnel_plans(), v: fmtNumber(s.funnel30d.plansCreated) },
                { k: m.admin_funnel_prd(), v: fmtNumber(s.funnel30d.prdCompleted) },
                { k: m.admin_funnel_verified(), v: fmtNumber(s.funnel30d.tasksVerified) },
                { k: m.admin_funnel_done(), v: fmtNumber(s.funnel30d.tasksDone) },
                { k: m.admin_funnel_evidence_ratio(), v: fmtPercent(s.funnel30d.evidenceRatio) },
                { k: m.admin_funnel_activation(), v: fmtPercent(s.funnel30d.activation) },
                { k: m.admin_funnel_verified_week(), v: fmtNumber(s.funnel30d.verifiedPerWeek) },
                { k: m.admin_funnel_share_views(), v: fmtNumber(s.funnel30d.shareViews) },
                { k: m.admin_funnel_exports(), v: fmtNumber(s.funnel30d.exports) },
              ]}
            />
          ) : (
            <p className="text-sm text-muted-foreground">{m.common_loading()}</p>
          )}
        </Panel>
        <Panel title={m.admin_health()}>
          {h ? (
            <KV
              items={[
                { k: 'Postgres', v: <Health ok={h.db.ok} detail={h.db.ok ? `${h.db.latencyMs} ms` : h.db.error} /> },
                { k: m.admin_health_gateway(), v: <Health ok={h.gateway.ok} detail={h.gateway.ok ? `${h.gateway.latencyMs} ms · ${h.gateway.models ?? '?'} models` : h.gateway.error} /> },
                { k: 'URL', v: <span className="font-mono text-xs">{h.gateway.baseUrl}</span> },
              ]}
            />
          ) : (
            <p className="text-sm text-muted-foreground">{m.common_loading()}</p>
          )}
        </Panel>
      </div>
    </AdminShell>
  )
}

function Health({ ok, detail }: { ok: boolean; detail?: string }) {
  return (
    <span className="flex items-center gap-2">
      <Badge variant="outline" className={ok ? 'border-status-done text-status-done' : 'border-status-failed text-status-failed'}>
        {ok ? m.admin_health_ok() : m.admin_health_down()}
      </Badge>
      <span className="truncate text-xs text-muted-foreground">{detail}</span>
    </span>
  )
}
